import { action, makeObservable, observable, runInAction } from 'mobx';
import { RootStore } from './index';
import { BaseStore } from './base.store';
import { IStore } from '../shared/common';
import { ActivityDto, AuditFiltersResponse } from '../../dtos/activity.dto';
import activityService from '../../services/activity.service';
import { ActivityCriteria, ActivityLogDownloadInfo } from '../../dtos/search-criteria.dto';
import { ActivityActionsRequest, LevelName, TimeRange } from '../../utils/enums';
import {
  buildActivityDownloadParams,
  buildUrlParams,
  convertCriteriaToRequest,
  convertResponseToActivity,
  triggerFileDownload
} from '../../utils/activity-util';
import { ExportOptions } from '../features/reports-and-logs/logs/export/export-options';
import { EventType } from 'src/dtos/analytics.dto';
import { calcActionDuration } from 'src/utils/constants';

export class ActivityStore extends BaseStore implements IStore<ActivityDto> {

  public data: ActivityDto[] | undefined = undefined;
  public totalRecords: number | undefined = undefined;
  public isLoading: boolean = false;
  public sortBy: string = 'auditTimestamp';
  public sortOrder: string = 'desc';
  public filters: AuditFiltersResponse = {} as AuditFiltersResponse;
  public searchCriteria: ActivityCriteria = {
    timeDetails: {
      timeRange: TimeRange.LAST_7_DAYS
    },
    activity: [],
    policyVersion: []
  };
  public exportLogMethod = ExportOptions[0];

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      data: observable,
      isLoading: observable,
      searchCriteria: observable,
      totalRecords: observable,
      sortBy: observable,
      sortOrder: observable,
      load: action,
      loadAudit: action,
      setSearchCriteria: action,
      filters: observable,
      exportLogMethod: observable,
      setExportMethod: action,
      setSortDetails: action
    });
  }

  public async load() {
    this.isLoading = true;
    void activityService.loadFilters(this.rootStore.customerStore.customerId,
      this.rootStore.customerStore.serviceId).then(res => {
        runInAction(() => {
          this.filters = res;
        });
      });
    await this.loadAudit();
  }

  public async loadAudit() {
    const startTime = performance.now();

    try {
      if (this.data === undefined) {
        this.isLoading = true;
      }
      const res = await activityService.getActivity(
        this.rootStore.customerStore.customerId,
        this.rootStore.customerStore.serviceId,
        convertCriteriaToRequest(this.searchCriteria),
        buildUrlParams(this.sortBy, this.sortOrder)
      );
      this.sendActivityEvent(EventType.ActivityLogsLoad, { actionDuration: calcActionDuration(startTime) });
      runInAction(() => {
        this.data = convertResponseToActivity(res);
        this.totalRecords = res.length;
        this.isLoading = false;
      });
    }
    catch (e) {
      this.sendActivityEvent(EventType.ActivityLogsLoad, { error: e?.toString(), actionDuration: calcActionDuration(startTime) });
      this.isLoading = false;
      throw e;
    }
  }

  public setExportMethod(value: any) {
    this.exportLogMethod = ExportOptions.find(e => e.label === value)!;
  }

  public setSortDetails(sortBy: string, sortOrder: string) {
    this.sortBy = sortBy;
    this.sortOrder = sortOrder.toLowerCase();
  }

  public async sendActivityLog(activity: ActivityActionsRequest,
    customerId = this.rootStore.customerStore.customerId,
    serviceId = this.rootStore.customerStore.serviceId) {
    const service = await this.rootStore.userStore.getServiceById(customerId, serviceId);
    if (!service || service.serviceLevel === LevelName.BRONZE) return;
    await activityService.writeActivityLog(activity, customerId, serviceId);
  }

  public async downloadLogs(): Promise<any> {
    const customerId = this.rootStore.customerStore.customerId;
    const serviceId = this.rootStore.customerStore.serviceId;

    const activityDownloadInfo: ActivityLogDownloadInfo | null = buildActivityDownloadParams(this.exportLogMethod.key, this.searchCriteria);
    if (!activityDownloadInfo) return;

    const res = await activityService.downloadActivityLogs(customerId, serviceId, activityDownloadInfo);
    triggerFileDownload(res.blob, res.fileName);
  }

  public setSearchCriteria(searchCriteriaDto: ActivityCriteria) {
    this.searchCriteria = searchCriteriaDto;
  }

  public updateData(newData: ActivityDto[]) {
    if (!this.data) {
      this.data = [...newData];
    } else {
      this.data = [...this.data, ...newData];
    }
  }

  public sendActivityEvent(eventType: EventType, extraInfo?: object): void {
    this.sendEvent(eventType, { filters: this.searchCriteria, ...extraInfo });
  }

  public reset() {
    this.data = undefined;
    this.totalRecords = undefined;
    this.isLoading = false;
    this.sortBy = 'auditTimestamp';
    this.sortOrder = 'desc';
    this.filters = {} as AuditFiltersResponse;
    this.searchCriteria = {
      timeDetails: {
        timeRange: TimeRange.LAST_7_DAYS
      },
      activity: [],
      policyVersion: []
    };
    this.exportLogMethod = ExportOptions[0];
  }
}
